<template>
  <div id="app" class="text-slate-700 dark:text-slate-500 ">
    <el-config-provider :locale="zhCn">
      <!-- 加载状态 -->
      <div v-if="isLoading" class="loading-container">
        <el-loading-directive 
          v-loading="true" 
          element-loading-text="正在初始化系统数据..."
          element-loading-background="rgba(0, 0, 0, 0.8)"
          class="loading-wrapper"
        />
      </div>
      <!-- 主要内容 -->
      <router-view v-else />
    </el-config-provider>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import { ElMessage } from 'element-plus'
import { useAppStore } from '@/pinia/modules/app'
import { useVipStore } from '@/pinia/modules/vip'
import { usePropsStore } from '@/pinia/modules/props'
import { useUserStore } from '@/pinia/modules/user'
import { useAvatarStore } from '@/pinia/modules/avatar'
import { useTagsStore } from '@/pinia/modules/tags'
import { useChannelStore } from '@/pinia/modules/channel'
import { useDictionaryStore } from '@/pinia/modules/dictionary'
import { getWaitApprovalOrders } from '@/api/orderList'
import orderNotifyAudio from '@/assets/audio/order_notify.mp4'

const appStore = useAppStore()
const vipStore = useVipStore()
const propsStore = usePropsStore()
const userStore = useUserStore()
const avatarStore = useAvatarStore()
const tagsStore = useTagsStore()
const channelStore = useChannelStore()
const dictionaryStore = useDictionaryStore()

// 加载状态
const isLoading = ref(false)

// 轮询定时器
let pollingTimer = null

// 音频对象
const notificationAudio = new Audio(orderNotifyAudio)

defineOptions({
  name: 'App'
})

// 检查待处理订单
const checkWaitApprovalOrders = async () => {
  try {
    const res = await getWaitApprovalOrders()

    // 更新待处理订单数到全局状态
    console.log(res.data)
    const orderCount = res.data.order_count
    appStore.setAllOrderCount(res.data.order_count)
    appStore.setWaitApprovalOrderCount(res.data.order_processing_count)
    appStore.setWaitApprovalOrderTradingCount(res.data.order_trading_count)
    appStore.setWaitApprovalOrderFailureCount(res.data.order_failure_count)
    appStore.setAbnormalRechargeOrderCount(res.data.abnormal_recharge_order_count || 0)

    // 如果有待处理订单，播放音频提醒
    if (orderCount > 0) {
      // 播放音频
      notificationAudio.play().catch(error => {
        console.warn('音频播放失败:', error)
      })

      // 显示通知消息
      ElMessage({
        type: 'warning',
        message: `您有 ${orderCount} 个待处理订单需要审核！`,
        duration: 5000,
        showClose: true
      })
    }
  } catch (error) {
    console.error('检查待处理订单失败:', error)
  }
}

// 启动轮询
const startPolling = () => {
  // 立即执行一次
  checkWaitApprovalOrders()

  // 每30秒轮询一次
  pollingTimer = setInterval(() => {
    checkWaitApprovalOrders()
  }, 30000)
}

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
}

// 全局初始化数据
onMounted(async () => {
  if (userStore.token) {
    try {
      isLoading.value = true

      // 使用 Promise.all 并行执行所有初始化操作
      await Promise.all([
        vipStore.initVipData(),
        avatarStore.initAvatarData(),
        propsStore.initPropsData(),
        tagsStore.getTagsData(),
        channelStore.getLandPageGroup(),
        dictionaryStore.getLanguageConfig()
      ])

      console.log('所有初始化数据加载完成')

      // 启动待处理订单轮询
      startPolling()
    } catch (error) {
      console.error('初始化数据失败:', error)
      // 可以在这里添加错误处理，比如显示错误提示
    } finally {
      isLoading.value = false
    }
  }
})

// 组件卸载时清理定时器
onBeforeUnmount(() => {
  stopPolling()
})
</script>
<style lang="scss">
// 引入初始化样式
#app {
  height: 100vh;
  overflow: hidden;
  font-weight: 400 !important;
}

.el-button {
  font-weight: 400 !important;
}

.gva-body-h {
  min-height: calc(100% - 3rem);
}

.gva-container {
  height: calc(100% - 2.5rem);
}

.gva-container2 {
  height: calc(100% - 4.5rem);
}

.vxe-tools--wrapper {
  margin-right: 10px;
}


.el-descriptions__content {
  width: 340px !important;
}

.w-e-full-screen-container {
  z-index: 10000 !important;
}

// 加载页面样式
.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
}

.loading-wrapper {
  width: 100%;
  height: 100%;
}
</style>
